import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { PracticeAreaForm, PracticeAreaType } from 'types/practice-areas';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { useFeatureFlags } from 'utils/useFeatureFlags';

export const PRACTICE_AREA_FORM_CLASSIFIERS = ['PRACTICE_AREA_TYPE'];

const defaultValues: PracticeAreaForm = {
    name: '',
    isActive: true,
    practiceAreaType: PracticeAreaType.PRACTICE_AREA,
    parent: null,
    isAmlApplicable: false,
    isExcludedFromAutomaticClosing: false,
    applyExclusionToSubareas: false,
    showInTimecards: true,
    showInCases: true,
    showInEmployees: true,
    practiceGroup: null,
    practiceTeams: [],
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

type OptionType = {
    label: string;
    value: string;
};

interface Props {
    initialData?: PracticeAreaForm;
    classifiers: Record<string, ClassifierData>;
    entityCustomFields?: CustomField[];
}

/**
 * The practice area form's state, shared by the create and edit pages and the cards: the form takes the custom
 * fields' values from the fields it is given, and the practice group fills the teams it shows.
 */
export const usePracticeAreaForm = ({ initialData, classifiers, entityCustomFields = EMPTY_CUSTOM_FIELDS }: Props) => {
    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.PRACTICE_AREA,
        fields: entityCustomFields,
    });

    const methods = useForm<PracticeAreaForm>({
        defaultValues: initialData
            ? { ...defaultValues, ...initialData, ...initialCustomFieldsObject }
            : { ...defaultValues, ...initialCustomFieldsObject },
        values: initialData ? { ...initialData, ...initialCustomFieldsObject } : undefined,
    });
    const { control, setValue } = methods;

    const { isFlowEnabled: isSubPracticeAreaFeatureEnabled } = useFeatureFlags(FeatureFlag.SUB_PRACTICE_AREA);

    const composeRadioOptionsByFeatureFlag = (classifier: ClassifierData | undefined): OptionType[] => {
        const options =
            (getValidOptionsFromClassifier(classifier) as {
                label: string;
                value: string;
            }[]) ?? [];

        if (!isSubPracticeAreaFeatureEnabled) {
            return options.filter((opt) => opt.value !== PracticeAreaType.PRACTICE_SUBAREA);
        }

        return options;
    };

    // Watch value for dynamic form change
    const [practiceAreaType, practiceGroup] = useWatch({ control, name: ['practiceAreaType', 'practiceGroup'] });

    const {
        data: practiceTeamListQueryData,
        isLoading: isLoadingPracticeTeamListQuery,
        isFetching: isFetchingPracticeTeamListQuery,
    } = useGetMinTeamsListWithFiltersQuery(
        { pageSize: 2000, page: 0, filters: `parentTeam.id==${practiceGroup?.id}` },
        { skip: practiceGroup === null }
    );
    const isLoadingPracticeTeamList = isLoadingPracticeTeamListQuery || isFetchingPracticeTeamListQuery;

    const practiceTeamList = useMemo(() => {
        return practiceTeamListQueryData?.content;
    }, [practiceTeamListQueryData]);

    useEffect(() => {
        if (isLoadingPracticeTeamList) {
            return;
        }
        if (practiceGroup === null) {
            setValue('practiceTeams', []);
            return;
        }
        if (practiceTeamList !== undefined) {
            setValue('practiceTeams', practiceTeamList);
        }
    }, [practiceTeamList, isLoadingPracticeTeamList, practiceGroup, setValue]);

    return {
        methods,
        customFields,
        initialCustomFieldsObject,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoadingCustomFieldData,
        isSubPracticeAreaFeatureEnabled,
        practiceAreaType,
        practiceAreaTypeOptions: composeRadioOptionsByFeatureFlag(classifiers['PRACTICE_AREA_TYPE']),
    };
};

export type PracticeAreaFormState = ReturnType<typeof usePracticeAreaForm>;
